import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule, FormControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { MatDividerModule } from "@angular/material/divider";
import { NgSelectModule } from "@ng-select/ng-select";
import SubRouteAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity";
import { SubRouteService } from "../sub-route.service";
import { UserMasterService } from "../sub-route-collaborator/user_master.service";
import { IChangeMainCollaborator } from "./interfaces/change-main-collaborator.interface";
import { IUserMaster, ICurrentUser } from "./interfaces/user-master.interface";
import { firstValueFrom } from "rxjs";
import { SubRouteTypeEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { IUpdateSubRoute } from "../interfaces/update-sub-route.interface";

@Component({
  selector: "app-change-main-collaborator-modal",
  templateUrl: "./change-main-collaborator-modal.component.html",
  styleUrls: ["./change-main-collaborator-modal.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDividerModule,
    NgSelectModule
  ],
})
export class ChangeMainCollaboratorModalComponent implements OnInit {
  subRoute: SubRouteAttributes;
  mainCollabList: IUserMaster[] = [];
  userMasterList: IUserMaster[] = [];
  selectedUser: IUserMaster | null = null;
  currentUser: ICurrentUser | null = null;
  isLoading = false;
  
  // Reactive Form
  changeUserForm: FormGroup;
  userControl: FormControl<IUserMaster | null>;

  constructor(
    public dialogRef: MatDialogRef<ChangeMainCollaboratorModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { subRoute: SubRouteAttributes },
    private snackBar: MatSnackBar,
    private subRouteService: SubRouteService,
    private userMasterService: UserMasterService,
    private fb: FormBuilder
  ) {
    this.subRoute = data.subRoute;
    this.userControl = new FormControl<IUserMaster | null>(null, [Validators.required]);
    this.changeUserForm = this.fb.group({
      selectedUser: this.userControl
    });
  }

  ngOnInit(): void {
    this.fetchAllUserMaster().then(() => {
      this.setCurrentUser();
      this.setupUserSelection();
    });
  }

  setCurrentUser(): void {
    if (this.subRoute.mainCollaborator) {
      this.currentUser = {
        masterUserId: this.subRoute.mainCollaborator.masterUserId,
        businessPartner: this.subRoute.mainCollaborator.businessPartner
      };
    }
  }

  async fetchAllUserMaster(): Promise<void> {
    try {
      const resUser = await firstValueFrom(
        this.userMasterService.findToComboBox()
      );
      // TODO: corregir tipo correct en la respuesta porque si vienen estas propiedades
      this.userMasterList = resUser?.data?.map((user: any) => ({
        masterUserId: user.masterUserId,
        username: user.username,
        businessPartnerName: user.businessPartnerName,
        businessPartner: user.businessPartner
      }));
      
      const filteredUsers = [];
      const res: any = await firstValueFrom(
        this.userMasterService.findToComboBox()
      );
      
      const userIds = res.data.map((user: IUserMaster) => user.masterUserId.toString());
      const assignedResults = await this.checkIfUserAssignedToRoute(userIds);
      
      for (let i = 0; i < assignedResults.length; i++) {
        if (!assignedResults[i]) {
          filteredUsers.push(res.data[i]);
        }
      }
      
      this.mainCollabList = filteredUsers;
    } catch (err) {
      this.snackBar.open("Error al cargar usuarios disponibles", "Cerrar", {
        duration: 5000,
        verticalPosition: "top"
      });
    }
  }

  setupUserSelection(): void {
    let selectedUserMaster: IUserMaster | null = null;
    
    if (this.subRoute.mainCollaborator) {
      selectedUserMaster = this.userMasterList.find(
        (user) => user.masterUserId.toString() === this.subRoute.mainCollaborator.masterUserId.toString()
      ) || null;
      
      if (selectedUserMaster) {
        // Agregar el usuario actual a mainCollabList si no existe (replicando lógica de openEditModal)
        const existsInList = this.mainCollabList.find(
          u => u.masterUserId.toString() === selectedUserMaster!.masterUserId.toString()
        );
        if (!existsInList) {
          this.mainCollabList.push(selectedUserMaster);
        }
      }
    }
    
    // Establecer el FormControl con el objeto completo
    this.userControl.setValue(selectedUserMaster);
    this.selectedUser = selectedUserMaster;
  }

  private async checkIfUserAssignedToRoute(userIds: string[]): Promise<boolean[]> {
    try {
      const res = await firstValueFrom(
        this.subRouteService.findToMainCollaborator(userIds)
      );
      return userIds.map(userId => 
        res.data.some((user) => user.mainCollaborator?.masterUserId?.toString() === userId)
      );
    } catch (err) {
      this.snackBar.open(err.error.message || "Error al marcar si el usuario es asignado.","Cerrar",{ duration: 5000});
      return userIds.map(() => false);
    }
  }

  onUserSelectionChange(selectedUser: IUserMaster): void {
    this.selectedUser = selectedUser;
  }

  compareByMaster(item1: IUserMaster, item2: IUserMaster): boolean {
    return item1 && item2 ? item1.masterUserId === item2.masterUserId : item1 === item2;
  }

  getRouteCode(): string {
    if (this.subRoute.externalKeys?.code) {
      return this.subRoute.externalKeys.code;
    }
    return '-';
  }

  getRouteType(): string {
    if (this.subRoute.type === "01" as SubRouteTypeEnum) {
      return "Preventa";
    } else if (this.subRoute.type === "02" as SubRouteTypeEnum) {
      return "Autoventa";
    } else if (this.subRoute.type === "03" as SubRouteTypeEnum) {
      return "Reparto";
    }
    return this.subRoute.type || '-';
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onSave(): Promise<void> {
    if (this.isLoading) return;
    
    if (this.changeUserForm.invalid || !this.selectedUser) {
      this.snackBar.open("Por favor seleccione un usuario", "Cerrar", {
        duration: 5000,
        verticalPosition: "top"
      });
      return;
    }

    if (this.selectedUser.masterUserId === this.currentUser?.masterUserId?.toString()) {
      this.snackBar.open("El usuario seleccionado es el mismo que el actual", "Cerrar", {
        duration: 5000,
        verticalPosition: "top"
      });
      return;
    }

    this.isLoading = true;

    const updateData: Partial<IUpdateSubRoute> = {
      name: this.subRoute.name,
      type: this.subRoute.type,
      mainCollaborator: {
        masterUserId: this.selectedUser.masterUserId
      }
    };

    this.subRouteService.update(this.subRoute._id.toString(), updateData).subscribe({
      next: () => {
        this.snackBar.open("Usuario asociado actualizado con éxito", "Cerrar", {
          duration: 5000,
          verticalPosition: "top"
        });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.snackBar.open(
          err.error.message || "Error al actualizar el usuario asociado",
          "Cerrar",
          { duration: 5000, verticalPosition: "top" }
        );
      }
    });
  }
}